/**
 * `scores.json`, in the static page's format, so the study collects one shape
 * from both artifacts. Content-free by construction: opaque item ids, enum
 * values, timestamps. Nothing a person typed is ever in it.
 */
import type { Cohort, Ledger, LedgerItem, Surface } from '@cordelia/core'
import type { Judgment } from './fields.js'

export type ExportedItem = {
  readonly item_id: string
  readonly section: Surface
  readonly cohort: Cohort
  /** The card carried a reply-owed signal. From the ledger, never asked. */
  readonly reply_owed: boolean
  /** The card carried an unsent-draft signal. From the ledger, never asked. */
  readonly draft: boolean
  readonly evidence: string | null
  readonly openness: string | null
  readonly org_attribution: string | null
  readonly modality_matches: string | null
  readonly action: string | null
  readonly correction_reason: string | null
  readonly history: string | null
  readonly updated_at: string | null
}

export type ScoresExport = {
  readonly version: 1
  readonly exported_at: string
  readonly cutoff: string
  readonly items: readonly ExportedItem[]
}

const ACTIVE: readonly Surface[] = ['you-owe', 'waiting-on', 'may-be-expected']

/** The cards the page shows, in the page's order: the three active sections, then Probably done?. */
export function displayedItems(ledger: Ledger, probablyDoneCap = 5): readonly LedgerItem[] {
  const active = ACTIVE.flatMap((s) => ledger.items.filter((i) => i.surface === s))
  const probablyDone = ledger.items.filter(
    (i) => i.surface === 'history' && i.history_rank !== null && i.history_rank < probablyDoneCap,
  )
  return [...active, ...probablyDone]
}

/** Cards with at least one judgment. */
export function scoredCount(scores: ReadonlyMap<string, Judgment>): number {
  let n = 0
  for (const j of scores.values()) if (Object.keys(j).some((k) => k !== 'updated_at')) n++
  return n
}

export function buildScoresExport(
  ledger: Ledger,
  cutoff: string,
  scores: ReadonlyMap<string, Judgment>,
  exportedAt = new Date().toISOString(),
  probablyDoneCap = 5,
): ScoresExport {
  const replyOwed = new Set(ledger.reply_owed_signals.map((r) => r.item_id))
  const drafts = new Set(ledger.draft_signals.map((d) => d.item_id))
  return {
    version: 1,
    exported_at: exportedAt,
    cutoff,
    items: displayedItems(ledger, probablyDoneCap).map((item) => {
      const s = scores.get(item.id) ?? {}
      return {
        item_id: item.id,
        section: item.surface,
        cohort: item.cohort,
        reply_owed: replyOwed.has(item.id),
        draft: drafts.has(item.id),
        evidence: s.evidence ?? null,
        openness: s.openness ?? null,
        org_attribution: s.org_attribution ?? null,
        modality_matches: s.modality_matches ?? null,
        action: s.action ?? null,
        correction_reason: s.correction_reason ?? null,
        history: s.history ?? null,
        updated_at: s.updated_at ?? null,
      }
    }),
  }
}

/** Hands the browser a file to save. Nothing is sent anywhere. */
export function downloadJson(name: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    URL.revokeObjectURL(url)
    a.remove()
  }, 0)
}
